// @ts-ignore - this can be easily replaced with arrow functions
import autoBind from "react-autobind";
import React from "react";
import cloneDeep from "lodash.clonedeep";
import clamp from "lodash.clamp";
import buffer from "buffer";
import get from "lodash.get";
import { unset } from "lodash";
import { arrayMoveMutable } from "array-move";
import hash from "string-hash";
import {
  Map,
  LayerSpecification,
  StyleSpecification,
  ValidationError,
  SourceSpecification,
} from "maplibre-gl";
import { latest, validateStyleMin } from "@maplibre/maplibre-gl-style-spec";

import MapMaplibreGl from "./MapMaplibreGl";
import MapOpenLayers from "./MapOpenLayers";
import LayerList from "./LayerList";
import LayerEditor from "./LayerEditor";
import AppToolbar, { MapState } from "./AppToolbar";
import AppLayout from "./AppLayout";
import SimplePanel, { SimpleSelection } from "./simple/SimplePanel";
import type { PreviewSession } from "./simple/usePreviewSession";
import { classifyLayer } from "../libs/simple/classify";
import { spotlight } from "../libs/simple/spotlight";
import { createUrlWriter } from "../libs/url-writer";
import MessagePanel from "./AppMessagePanel";

import ModalSettings from "./ModalSettings";
import ModalExport from "./ModalExport";
import ModalSources from "./ModalSources";
import ModalOpen from "./ModalOpen";
import ModalSave from "./ModalSave";
import ModalShortcuts from "./ModalShortcuts";
import ModalDebug from "./ModalDebug";

import {
  downloadGlyphsMetadata,
  downloadSpriteMetadata,
} from "../libs/metadata";
import style from "../libs/style";
import {
  initialStyleUrl,
  loadStyleUrl,
  removeStyleQuerystring,
} from "../libs/urlopen";
import { undoMessages, redoMessages } from "../libs/diffmessage";
import { StyleStore } from "../libs/stylestore";
import { ApiStyleStore } from "../libs/apistore";
import {
  EditorMode,
  StudioTheme,
  getEditorMode,
  followSystemTheme,
  getTheme,
  setEditorMode,
  setTheme,
} from "../libs/preferences";
import { RevisionStore } from "../libs/revisions";
import LayerWatcher from "../libs/layerwatcher";
import tokens from "../config/tokens.json";
import isEqual from "lodash.isequal";
import Debug from "../libs/debug";
import { SortEnd } from "react-sortable-hoc";
import { MapOptions } from "maplibre-gl";

// Every style change is written into the address, also each step of a colour
// drag; Safari throws once that happens more than 100 times in 10 seconds.
const writeUrl = createUrlWriter(() => history.replaceState.bind(history), 150);

// Buffer must be defined globally for @maplibre/maplibre-gl-style-spec validate() function to succeed.
window.Buffer = buffer.Buffer;

function setFetchAccessToken(url: string, mapStyle: StyleSpecification) {
  const matchesTilehosting = url.match(/\.tilehosting\.com/);
  const matchesMaptiler = url.match(/\.maptiler\.com/);
  const matchesThunderforest = url.match(/\.thunderforest\.com/);
  if (matchesTilehosting || matchesMaptiler) {
    const accessToken = style.getAccessToken("openmaptiles", mapStyle, {
      allowFallback: true,
    });
    if (accessToken) {
      return url.replace("{key}", accessToken);
    }
  } else if (matchesThunderforest) {
    const accessToken = style.getAccessToken("thunderforest", mapStyle, {
      allowFallback: true,
    });
    if (accessToken) {
      return url.replace("{key}", accessToken);
    }
  } else {
    return url;
  }
}

function updateRootSpec(spec: any, fieldName: string, newValues: any) {
  return {
    ...spec,
    $root: {
      ...spec.$root,
      [fieldName]: {
        ...spec.$root[fieldName],
        values: newValues,
      },
    },
  };
}

type OnStyleChangedOpts = {
  save?: boolean;
  addRevision?: boolean;
  initialLoad?: boolean;
};

type MappedErrors = {
  message: string;
  parsed?: {
    type: string;
    data: {
      index: number;
      key: string;
      message: string;
    };
  };
};

type AppState = {
  errors: MappedErrors[];
  infos: string[];
  mapStyle: StyleSpecification & { id: string };
  dirtyMapStyle?: StyleSpecification;
  selectedLayerIndex: number;
  selectedLayerOriginalId?: string;
  sources: { [key: string]: SourceSpecification };
  vectorLayers: {};
  spec: any;
  mapView: {
    zoom: number;
    center: {
      lng: number;
      lat: number;
    };
  };
  maplibreGlDebugOptions: Partial<MapOptions> & {
    showTileBoundaries: boolean;
    showCollisionBoxes: boolean;
    showOverdrawInspector: boolean;
  };
  openlayersDebugOptions: {
    debugToolbox: boolean;
  };
  mapState: MapState;
  editorMode: EditorMode;
  theme: StudioTheme;
  simpleSelection: SimpleSelection;
  // Layers that stay bright while the user points at a colour in Simple
  // mode; the rest of the map fades. Display only, never saved.
  simpleSpotlight: string[] | null;
  isOpen: {
    settings: boolean;
    sources: boolean;
    open: boolean;
    save: boolean;
    shortcuts: boolean;
    export: boolean;
    debug: boolean;
    ai: boolean;
  };
  customStyles: {
    files: any[];
  };
};

// Dialogs that save, export or replace the style, or edit it as a whole.
// They work on the style as it is on screen, so a Simple-mode change still
// being previewed is made permanent before one of them opens.
const MODALS_SETTLING_PREVIEW: (keyof AppState["isOpen"])[] = [
  "save",
  "export",
  "open",
  "sources",
  "settings",
];

// How long an undo or redo message stays in the bottom panel.
const INFO_DURATION_MS = 6000;

// Fields inside the Simple panel that have an undo of their own.
const SIMPLE_TEXT_FIELDS =
  ".studio-simple input, .studio-simple textarea, .studio-simple [contenteditable]:not([contenteditable='false'])";
// Inputs that hold no text, so Cmd/Ctrl+Z on them still means "undo the map".
const NON_TEXT_INPUTS =
  "input[type='range'], input[type='checkbox'], input[type='radio'], input[type='button'], input[type='color']";

export default class App extends React.Component<any, AppState> {
  revisionStore: RevisionStore;
  styleStore: StyleStore | ApiStyleStore;
  layerWatcher: LayerWatcher;
  // The Simple panel's preview session, while that panel is mounted.
  simpleSession: PreviewSession | null = null;
  infoTimer: number | undefined;
  stopFollowingSystemTheme: (() => void) | undefined;

  constructor(props: any) {
    super(props);
    autoBind(this);

    this.revisionStore = new RevisionStore();
    const params = new URLSearchParams(window.location.search.substring(1));
    let port = params.get("localport");
    if (
      port == null &&
      window.location.port !== "80" &&
      window.location.port !== "443"
    ) {
      port = window.location.port;
    }
    this.styleStore = new ApiStyleStore({
      onLocalStyleChange: (mapStyle) =>
        this.onStyleChanged(mapStyle, { save: false }),
      port: port,
      host: params.get("localhost"),
    });

    const shortcuts = [
      {
        key: "?",
        handler: () => {
          this.toggleModal("shortcuts");
        },
      },
      {
        key: "o",
        handler: () => {
          this.toggleModal("open");
        },
      },
      {
        key: "s",
        handler: () => {
          this.toggleModal("save");
        },
      },
      {
        key: "e",
        handler: () => {
          this.toggleModal("export");
        },
      },
      {
        key: "d",
        handler: () => {
          this.toggleModal("sources");
        },
      },
      {
        key: "p",
        handler: () => {
          this.toggleModal("settings");
        },
      },
      {
        key: "i",
        handler: () => {
          this.setMapState(this.state.mapState === "map" ? "inspect" : "map");
        },
      },
      {
        key: "m",
        handler: () => {
          (
            document.querySelector(".maplibregl-canvas") as HTMLCanvasElement
          ).focus();
        },
      },
      {
        key: "!",
        handler: () => {
          this.toggleModal("debug");
        },
      },
    ];

    document.body.addEventListener("keyup", (e) => {
      if (e.key === "Escape") {
        (e.target as HTMLElement).blur();
        document.body.focus();
      } else if (
        this.state.isOpen.shortcuts ||
        document.activeElement === document.body
      ) {
        const shortcut = shortcuts.find((shortcut) => {
          return shortcut.key === e.key;
        });

        if (shortcut) {
          this.setModal("shortcuts", false);
          shortcut.handler();
        }
      }
    });

    const styleUrl = initialStyleUrl();
    const extractFileName = (styleUrl: string) => styleUrl?.split?.('?fileName=')?.[1]?.split?.('/')?.[1]?.split?.('&token=')?.[0];
    if (
      styleUrl &&
      window.confirm(
        "Load style from URL: " + extractFileName(styleUrl) + " and discard current changes?"
      )
    ) {
      this.styleStore = new StyleStore();
      loadStyleUrl(styleUrl, (mapStyle) => this.onStyleChanged(mapStyle));
      removeStyleQuerystring();
    } else {
      if (styleUrl) {
        removeStyleQuerystring();
      }
      this.styleStore.init((err) => {
        if (err) {
          console.log("Falling back to local storage for storing styles");
          this.styleStore = new StyleStore();
        }
        this.styleStore.latestStyle((mapStyle) =>
          this.onStyleChanged(mapStyle, { initialLoad: true })
        );

        if (Debug.enabled()) {
          Debug.set("atlas-studio", "styleStore", this.styleStore);
          Debug.set("atlas-studio", "revisionStore", this.revisionStore);
        }
      });
    }

    if (Debug.enabled()) {
      Debug.set("atlas-studio", "revisionStore", this.revisionStore);
      Debug.set("atlas-studio", "styleStore", this.styleStore);
    }

    this.state = {
      errors: [],
      infos: [],
      mapStyle: style.emptyStyle,
      selectedLayerIndex: 0,
      sources: {},
      vectorLayers: {},
      mapState: "map",
      editorMode: getEditorMode(),
      theme: getTheme(),
      simpleSelection: null,
      simpleSpotlight: null,
      spec: latest,
      mapView: {
        zoom: 0,
        center: {
          lng: 0,
          lat: 0,
        },
      },
      isOpen: {
        settings: false,
        sources: false,
        open: false,
        save: false,
        shortcuts: false,
        export: false,
        // TODO: Disabled for now, this should be opened on the Nth visit to the editor
        debug: false,
        ai: false,
      },
      maplibreGlDebugOptions: {
        showTileBoundaries: false,
        showCollisionBoxes: false,
        showOverdrawInspector: false,
      },
      openlayersDebugOptions: {
        debugToolbox: false,
      },
      customStyles: {
        files: [],
      },
    };

    this.layerWatcher = new LayerWatcher({
      onVectorLayersChange: (v) => this.setState({ vectorLayers: v }),
    });
  }

  handleKeyPress = (e: KeyboardEvent) => {
    // In a text field of the Simple panel the combination belongs to the
    // browser: it undoes the typing, not the map.
    const target = e.target;
    if (
      target instanceof Element &&
      target.matches(SIMPLE_TEXT_FIELDS) &&
      !target.matches(NON_TEXT_INPUTS)
    ) {
      return;
    }

    if (navigator.platform.toUpperCase().indexOf("MAC") >= 0) {
      if (e.metaKey && e.shiftKey && e.keyCode === 90) {
        e.preventDefault();
        this.onRedo();
      } else if (e.metaKey && e.keyCode === 90) {
        e.preventDefault();
        this.onUndo();
      }
    } else {
      if (e.ctrlKey && e.keyCode === 90) {
        e.preventDefault();
        this.onUndo();
      } else if (e.ctrlKey && e.keyCode === 89) {
        e.preventDefault();
        this.onRedo();
      }
    }
  };

  // Helper function to set cookies with expiration
  setCookie(name: string, value: string, days: number) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000); // Set expiration time
    const expires = "expires=" + date.toUTCString();
    document.cookie = `${name}=${value};${expires};path=/`; // Store cookie
  }

  // Helper function to get cookies
  getCookie(name: string) {
    const decodedCookie = decodeURIComponent(document.cookie);
    const cookieArr = decodedCookie.split(";");
    for (let i = 0; i < cookieArr.length; i++) {
      let cookie = cookieArr[i].trim();
      if (cookie.indexOf(name) === 0) {
        return cookie.substring(name.length + 1, cookie.length);
      }
    }
    return "";
  }

  // Function to handle query parameter and update the URL
  handleSessionParam() {
    // Step 1: Get the query string
    const queryString = window.location.search;

    // Step 2: Parse the query string
    const params = new URLSearchParams(queryString);
    const session = params.get("session"); // Replace "session" with your parameter name

    // Step 3: Check if session is in the URL or cookies
    if (session) {
      // If session param is found in URL, set it in a cookie (expires in 1 day)
      this.setCookie("session", session, 1);

      // Remove the session query parameter from the URL
      params.delete("session"); // Remove the session parameter
      const newUrl = window.location.pathname + "?" + params.toString();
      window.history.replaceState(null, "", newUrl);
    } else {
      // If no session in URL, check cookies
      const sessionFromCookie = this.getCookie("session");

      // If no session in both URL and cookies, redirect to the portal
      if (!sessionFromCookie) {
        window.location.href = "https://portal.mapmetrics.org/studio?notify=true";
      }
    }
  }

  componentDidMount() {
    window.addEventListener("keydown", this.handleKeyPress);
    this.stopFollowingSystemTheme = followSystemTheme((theme) =>
      this.setState({ theme })
    );
    window.onbeforeunload = function (event) {
      // Add Logic to save file over here...
      event.preventDefault();
    };
    this.handleSessionParam();
    this.getAllUserFiles();
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.handleKeyPress);
    this.stopFollowingSystemTheme?.();
    window.clearTimeout(this.infoTimer);
  }

  saveStyle(snapshotStyle: StyleSpecification & { id: string }) {
    this.styleStore.save(snapshotStyle);
  }

  updateFonts(urlTemplate: string) {
    const metadata: { [key: string]: string } =
      this.state.mapStyle.metadata || ({} as any);
    const accessToken =
      metadata["atlas-studio:openmaptiles_access_token"] || tokens.openmaptiles;

    const glyphUrl =
      typeof urlTemplate === "string"
        ? urlTemplate.replace("{key}", accessToken)
        : urlTemplate;
    downloadGlyphsMetadata(glyphUrl, (fonts) => {
      this.setState({ spec: updateRootSpec(this.state.spec, "glyphs", fonts) });
    });
  }

  updateIcons(baseUrl: string) {
    downloadSpriteMetadata(baseUrl, (icons) => {
      this.setState({ spec: updateRootSpec(this.state.spec, "sprite", icons) });
    });
  }

  onChangeMetadataProperty = (property: string, value: any) => {
    // If we're changing renderer reset the map state.
    if (
      property === "atlas-studio:renderer" &&
      value !==
        get(this.state.mapStyle, ["metadata", "atlas-studio:renderer"], "mlgljs")
    ) {
      this.setState({
        mapState: "map",
      });
    }

    const changedStyle = {
      ...this.state.mapStyle,
      metadata: {
        ...(this.state.mapStyle as any).metadata,
        [property]: value,
      },
    };
    this.onStyleChanged(changedStyle);
  };

  onStyleChanged = (
    newStyle: StyleSpecification & { id: string },
    opts: OnStyleChangedOpts = {}
  ) => {
    opts = {
      save: true,
      addRevision: true,
      initialLoad: false,
      ...opts,
    };

    if (opts.initialLoad) {
      this.getInitialStateFromUrl(newStyle);
    }

    const errors: ValidationError[] = validateStyleMin(newStyle) || [];

    // The validate function doesn't give us errors for duplicate error with
    // empty string for layer.id, manually deal with that here.
    const layerErrors: (Error | ValidationError)[] = [];
    if (newStyle && newStyle.layers) {
      const foundLayers = new global.Map();
      newStyle.layers.forEach((layer, index) => {
        if (layer.id === "" && foundLayers.has(layer.id)) {
          const error = new Error(
            `layers[${index}]: duplicate layer id [empty_string], previously used`
          );
          layerErrors.push(error);
        }
        foundLayers.set(layer.id, true);
      });
    }

    const mappedErrors = layerErrors.concat(errors).map((error) => {
      // Special case: Duplicate layer id
      const dupMatch = error.message.match(
        /layers\[(\d+)\]: (duplicate layer id "?(.*)"?, previously used)/
      );
      if (dupMatch) {
        const [, index, message] = dupMatch;
        return {
          message: error.message,
          parsed: {
            type: "layer",
            data: {
              index: parseInt(index, 10),
              key: "id",
              message,
            },
          },
        };
      }

      // Special case: Invalid source
      const invalidSourceMatch = error.message.match(
        /layers\[(\d+)\]: (source "(?:.*)" not found)/
      );
      if (invalidSourceMatch) {
        const [, index, message] = invalidSourceMatch;
        return {
          message: error.message,
          parsed: {
            type: "layer",
            data: {
              index: parseInt(index, 10),
              key: "source",
              message,
            },
          },
        };
      }

      const layerMatch = error.message.match(
        /layers\[(\d+)\]\.(?:(\S+)\.)?(\S+): (.*)/
      );
      if (layerMatch) {
        const [, index, group, property, message] = layerMatch;
        const key = group && property ? [group, property].join(".") : property;
        return {
          message: error.message,
          parsed: {
            type: "layer",
            data: {
              index: parseInt(index, 10),
              key,
              message,
            },
          },
        };
      } else {
        return {
          message: error.message,
        };
      }
    });

    let dirtyMapStyle: StyleSpecification | undefined = undefined;
    if (errors.length > 0) {
      dirtyMapStyle = cloneDeep(newStyle);

      errors.forEach((error) => {
        const { message } = error;
        if (message) {
          try {
            const objPath = message.split(":")[0];
            // Errors can be deply nested for example 'layers[0].filter[1][1][0]' we only care upto the property 'layers[0].filter'
            const unsetPath = objPath.match(/^\S+?\[\d+\]\.[^[]+/)![0];
            unset(dirtyMapStyle, unsetPath);
          } catch (err) {
            console.warn(err);
          }
        }
      });
    }

    if (newStyle.glyphs !== this.state.mapStyle.glyphs) {
      this.updateFonts(newStyle.glyphs as string);
    }
    if (newStyle.sprite !== this.state.mapStyle.sprite) {
      this.updateIcons(newStyle.sprite as string);
    }

    if (opts.addRevision) {
      this.revisionStore.addRevision(newStyle);
    }
    if (opts.save) {
      this.saveStyle(newStyle as StyleSpecification & { id: string });
    }

    this.setState(
      {
        mapStyle: newStyle,
        dirtyMapStyle: dirtyMapStyle,
        errors: mappedErrors,
      },
      () => {
        this.fetchSources();
        this.setStateInUrl();
      }
    );
  };

  // Shows undo and redo messages, and takes them away again after a while.
  showInfos = (infos: string[]) => {
    window.clearTimeout(this.infoTimer);
    this.setState({ infos });
    this.infoTimer = window.setTimeout(() => {
      this.setState({ infos: [] });
    }, INFO_DURATION_MS);
  };

  onUndo = () => {
    // A change still being previewed in Simple mode is the newest thing on
    // screen, so it is what Undo takes back. No saved step is touched.
    if (this.simpleSession?.isPreviewing()) {
      this.simpleSession.cancel();
      return;
    }

    const activeStyle = this.revisionStore.undo();

    const messages = undoMessages(this.state.mapStyle, activeStyle);
    this.onStyleChanged(activeStyle, { addRevision: false });
    this.showInfos(messages);
  };

  onRedo = () => {
    const activeStyle = this.revisionStore.redo();
    const messages = redoMessages(this.state.mapStyle, activeStyle);
    this.onStyleChanged(activeStyle, { addRevision: false });
    this.showInfos(messages);
  };

  onSetEditorMode = (editorMode: EditorMode) => {
    setEditorMode(editorMode);
    window.clearTimeout(this.infoTimer);
    // Undo and redo messages are hidden in Simple mode; one from before the
    // switch would be stale by the time it showed.
    // Simple mode starts on its overview: an AI view left open in the URL
    // (a reload while in Advanced) must not be the first thing shown.
    const isOpen =
      editorMode === "simple"
        ? { ...this.state.isOpen, ai: false }
        : this.state.isOpen;
    this.setState({ editorMode, infos: [], isOpen }, this.setStateInUrl);
  };

  onToggleTheme = () => {
    const theme: StudioTheme = this.state.theme === "dark" ? "light" : "dark";
    setTheme(theme);
    this.setState({ theme });
  };

  // A change still in progress (dragging in a colour picker). Shown on the
  // map, but not saved and not an undo step until committed.
  onSimplePreview = (mapStyle: StyleSpecification & { id: string }) => {
    this.onStyleChanged(mapStyle, { addRevision: false, save: false });
  };

  onSimpleCommit = (mapStyle: StyleSpecification & { id: string }) => {
    this.onStyleChanged(mapStyle);
  };

  onSimpleSession = (session: PreviewSession | null) => {
    this.simpleSession = session;
  };

  onSimpleSpotlight = (simpleSpotlight: string[] | null) => {
    this.setState({ simpleSpotlight });
  };

  onSimpleSelect = (simpleSelection: SimpleSelection) => {
    this.setState({ simpleSelection });
  };

  onMapFeatureClick = (layerIds: string[]) => {
    const layers = this.state.mapStyle.layers || [];
    const hit = layerIds
      .map((id) => layers.find((layer) => layer.id === id))
      .find((layer) => layer !== undefined);
    // Nothing under the cursor means bare background, which belongs to Land.
    this.setState({
      simpleSelection: hit ? classifyLayer(hit).component : "land",
    });
  };

  onMoveLayer = (move: SortEnd) => {
    let { oldIndex, newIndex } = move;
    let layers = this.state.mapStyle.layers;
    oldIndex = clamp(oldIndex, 0, layers.length - 1);
    newIndex = clamp(newIndex, 0, layers.length - 1);
    if (oldIndex === newIndex) return;

    if (oldIndex === this.state.selectedLayerIndex) {
      this.setState({
        selectedLayerIndex: newIndex,
      });
    }

    layers = layers.slice(0);
    arrayMoveMutable(layers, oldIndex, newIndex);
    this.onLayersChange(layers);
  };

  onLayersChange = (changedLayers: LayerSpecification[]) => {
    const changedStyle = {
      ...this.state.mapStyle,
      layers: changedLayers,
    };
    this.onStyleChanged(changedStyle);
  };

  onLayerDestroy = (index: number) => {
    const layers = this.state.mapStyle.layers;
    const remainingLayers = layers.slice(0);
    remainingLayers.splice(index, 1);
    this.onLayersChange(remainingLayers);
  };

  onLayerCopy = (index: number) => {
    const layers = this.state.mapStyle.layers;
    const changedLayers = layers.slice(0);

    const clonedLayer = cloneDeep(changedLayers[index]);
    clonedLayer.id = clonedLayer.id + "-copy";
    changedLayers.splice(index, 0, clonedLayer);
    this.onLayersChange(changedLayers);
  };

  onLayerVisibilityToggle = (index: number) => {
    const layers = this.state.mapStyle.layers;
    const changedLayers = layers.slice(0);

    const layer = { ...changedLayers[index] };
    const changedLayout = "layout" in layer ? { ...layer.layout } : {};
    changedLayout.visibility =
      changedLayout.visibility === "none" ? "visible" : "none";

    layer.layout = changedLayout;
    changedLayers[index] = layer;
    this.onLayersChange(changedLayers);
  };

  onLayerIdChange = (index: number, _oldId: string, newId: string) => {
    const changedLayers = this.state.mapStyle.layers.slice(0);
    changedLayers[index] = {
      ...changedLayers[index],
      id: newId,
    };

    this.onLayersChange(changedLayers);
  };

  onLayerChanged = (index: number, layer: LayerSpecification) => {
    const changedLayers = this.state.mapStyle.layers.slice(0);
    changedLayers[index] = layer;

    this.onLayersChange(changedLayers);
  };

  setMapState = (newState: MapState) => {
    this.setState(
      {
        mapState: newState,
      },
      this.setStateInUrl
    );
  };

  setDefaultValues = (styleObj: StyleSpecification & { id: string }) => {
    const metadata: { [key: string]: string } =
      styleObj.metadata || ({} as any);
    if (metadata["atlas-studio:renderer"] === undefined) {
      const changedStyle = {
        ...styleObj,
        metadata: {
          ...(styleObj.metadata as any),
          "atlas-studio:renderer": "mlgljs",
        },
      };
      return changedStyle;
    } else {
      return styleObj;
    }
  };

  openStyle = (styleObj: StyleSpecification & { id: string }) => {
    styleObj = this.setDefaultValues(styleObj);
    this.onStyleChanged(styleObj);
  };

  fetchSources() {
    const sourceList: { [key: string]: any } = {};

    for (const [key, val] of Object.entries(this.state.mapStyle.sources)) {
      if (
        !Object.prototype.hasOwnProperty.call(this.state.sources, key) &&
        val.type === "vector" &&
        Object.prototype.hasOwnProperty.call(val, "url")
      ) {
        sourceList[key] = {
          type: val.type,
          layers: [],
        };

        let url = val.url;

        try {
          url = setFetchAccessToken(url!, this.state.mapStyle);
        } catch (err) {
          console.warn("Failed to setFetchAccessToken: ", err);
        }

        fetch(url!, {
          mode: "cors",
        })
          .then((response) => response.json())
          .then((json) => {
            if (!Object.prototype.hasOwnProperty.call(json, "vector_layers")) {
              return;
            }

            // Create new objects before setState
            const sources = Object.assign(
              {},
              {
                [key]: this.state.sources[key],
              }
            );

            for (const layer of json.vector_layers) {
              (sources[key] as any).layers.push(layer.id);
            }

            console.debug("Updating source: " + key);
            this.setState({
              sources: sources,
            });
          })
          .catch((err) => {
            console.error("Failed to process sources for '%s'", url, err);
          });
      } else {
        sourceList[key] =
          this.state.sources[key] || this.state.mapStyle.sources[key];
      }
    }

    if (!isEqual(this.state.sources, sourceList)) {
      console.debug("Setting sources");
      this.setState({
        sources: sourceList,
      });
    }
  }

  _getRenderer() {
    const metadata: { [key: string]: string } =
      this.state.mapStyle.metadata || ({} as any);
    return metadata["atlas-studio:renderer"] || "mlgljs";
  }

  onMapChange = (mapView: {
    zoom: number;
    center: {
      lng: number;
      lat: number;
    };
  }) => {
    this.setState({
      mapView,
    });
  };

  mapRenderer() {
    const { mapStyle, dirtyMapStyle, simpleSpotlight } = this.state;
    const shown = dirtyMapStyle || mapStyle;

    const mapProps = {
      mapStyle:
        simpleSpotlight && this.state.editorMode === "simple"
          ? spotlight(shown, new Set(simpleSpotlight))
          : shown,
      replaceAccessTokens: (mapStyle: StyleSpecification) => {
        return style.replaceAccessTokens(mapStyle, {
          allowFallback: true,
        });
      },
      onDataChange: (e: { map: Map }) => {
        this.layerWatcher.analyzeMap(e.map);
        this.fetchSources();
      },
    };

    const renderer = this._getRenderer();

    let mapElement;

    // Check if OL code has been loaded?
    if (renderer === "ol") {
      mapElement = (
        <MapOpenLayers
          {...mapProps}
          onChange={this.onMapChange}
          debugToolbox={this.state.openlayersDebugOptions.debugToolbox}
          onLayerSelect={this.onLayerSelect}
        />
      );
    } else {
      mapElement = (
        <MapMaplibreGl
          {...mapProps}
          onChange={this.onMapChange}
          options={this.state.maplibreGlDebugOptions}
          inspectModeEnabled={this.state.mapState === "inspect"}
          highlightedLayer={
            this.state.mapStyle.layers[this.state.selectedLayerIndex]
          }
          onLayerSelect={this.onLayerSelect}
          // Inspect view keeps its own popup and shows other layers than the
          // style, so clicking the map only opens a component outside it.
          simpleMode={
            this.state.editorMode === "simple" &&
            this.state.mapState !== "inspect"
          }
          onFeatureClick={this.onMapFeatureClick}
        />
      );
    }

    let filterName;
    if (this.state.mapState.match(/^filter-/)) {
      filterName = this.state.mapState.replace(/^filter-/, "");
    }
    const elementStyle: { filter?: string } = {};
    if (filterName) {
      elementStyle.filter = `url('#${filterName}')`;
    }

    return (
      <div
        style={elementStyle}
        className="studio-map__container"
        data-wd-key="maplibre:container"
      >
        {mapElement}
      </div>
    );
  }

  setStateInUrl = () => {
    const { mapState, mapStyle, isOpen } = this.state;
    const { selectedLayerIndex } = this.state;
    const url = new URL(location.href);
    const hashVal = hash(JSON.stringify(mapStyle));
    url.searchParams.set("layer", `${hashVal}~${selectedLayerIndex}`);

    const openModals = Object.entries(isOpen)
      .map(([key, val]) => (val === true ? key : null))
      .filter((val) => val !== null);

    if (openModals.length > 0) {
      url.searchParams.set("modal", openModals.join(","));
    } else {
      url.searchParams.delete("modal");
    }

    if (mapState === "map") {
      url.searchParams.delete("view");
    } else if (mapState === "inspect") {
      url.searchParams.set("view", "inspect");
    }

    writeUrl({ selectedLayerIndex }, url.href);
  };

  getInitialStateFromUrl = (mapStyle: StyleSpecification) => {
    const url = new URL(location.href);
    const modalParam = url.searchParams.get("modal");
    if (modalParam && modalParam !== "") {
      const modals = modalParam.split(",");
      const modalObj: { [key: string]: boolean } = {};
      modals.forEach((modalName) => {
        modalObj[modalName] = true;
      });

      this.setState({
        isOpen: {
          ...this.state.isOpen,
          ...modalObj,
        },
      });
    }

    const view = url.searchParams.get("view");
    if (view && view !== "") {
      this.setMapState(view as MapState);
    }

    const path = url.searchParams.get("layer");
    if (path) {
      try {
        const parts = path.split("~");
        const [hashVal, selectedLayerIndex] = [
          parts[0],
          parseInt(parts[1], 10),
        ];

        let valid = true;
        if (hashVal !== "-") {
          const currentHashVal = hash(JSON.stringify(mapStyle));
          if (currentHashVal !== parseInt(hashVal, 10)) {
            valid = false;
          }
        }
        if (valid) {
          this.setState({
            selectedLayerIndex,
            selectedLayerOriginalId: mapStyle.layers[selectedLayerIndex].id,
          });
        }
      } catch (err) {
        console.warn(err);
      }
    }
  };

  onLayerSelect = (index: number) => {
    this.setState(
      {
        selectedLayerIndex: index,
        selectedLayerOriginalId: this.state.mapStyle.layers[index].id,
      },
      this.setStateInUrl
    );
  };


  setSelectedInWindow = (styleData: any) => {
    try {
      window.localStorage.setItem(`atlas-studio:style:${styleData.id}`, JSON.stringify(styleData));
      window.localStorage.setItem("atlas-studio:latest_style", styleData.id);
      return { success: true, message: "Saved in window successfully!", error: null }
    } catch (error) {
      return {
        success: false, message: "Unable to save properly", error
      };
    }
  };

  async saveStyleToCloudflare(styleName: string) {
    const styleData = JSON.parse(JSON.stringify(this.state.mapStyle))
    if (styleName && styleName.trim() != "") {
      styleData.name = styleName;
    }
    
    try {
      // Send the file.
      const response = await fetch(
        `https://portal-server.jim9710.workers.dev/api/studio/styles/create`,
        // `http://localhost:3001/api/studio/styles/create`,
        {
          method: "POST",
          headers: {
            Authorization:
              "Bearer " + document.cookie.split("session=")[1]?.split(";")[0], // Pass down the same session from portal
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            styleName,
            styleData
          }),
        }
      );

      const data = await response.json().catch(() => null);
      if (!data) {
        return { success: false, message: `The style could not be saved (server answered ${response.status}). Please try again.` };
      }
      this.setSelectedInWindow(styleData);
      this.getAllUserFiles();
      // Accounts without API keys still save fine — the server only skips
      // key authorization, so treat that response as a successful save.
      if (data && data.success === false && typeof data.message === "string" && data.message.includes("No keys found")) {
        return { success: true, message: "Style saved." };
      }
      return data;
    } catch (error) {
      console.error("Error:", error);
      return { success: false, message: "Could not reach the server, so the style was not saved. Check your connection and try again." };
    }
  }

  async getAllUserFiles() {
    try {
      // Send the file.
      const response = await fetch(
        `https://portal-server.jim9710.workers.dev/api/studio/styles/get-all`,
        // `http://localhost:3001/api/studio/styles/get-all`,
        {
          method: "GET",
          headers: {
            Authorization:
              "Bearer " + document.cookie.split("session=")[1]?.split(";")[0], // Pass down the same session from portal
            "Content-Type": "application/json",
          },
        }
      );

      const { data } = await response.json();
      this.setState({ customStyles: data });
      return data;
    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  }

  setModal(modalName: keyof AppState["isOpen"], value: boolean) {
    // What you see is what you save: a preview on screen becomes a real,
    // undoable change before a dialog that works on the style opens.
    if (value && MODALS_SETTLING_PREVIEW.includes(modalName)) {
      this.simpleSession?.commit();
    }
    this.setState(
      {
        isOpen: {
          ...this.state.isOpen,
          [modalName]: value,
        },
      },
      this.setStateInUrl
    );
  }

  toggleModal(modalName: keyof AppState["isOpen"]) {
    this.setModal(modalName, !this.state.isOpen[modalName]);
  }

  onChangeOpenlayersDebug = (
    key: keyof AppState["openlayersDebugOptions"],
    value: boolean
  ) => {
    this.setState({
      openlayersDebugOptions: {
        ...this.state.openlayersDebugOptions,
        [key]: value,
      },
    });
  };

  onChangeMaplibreGlDebug = (
    key: keyof AppState["maplibreGlDebugOptions"],
    value: any
  ) => {
    this.setState({
      maplibreGlDebugOptions: {
        ...this.state.maplibreGlDebugOptions,
        [key]: value,
      },
    });
  };

  render() {
    const layers = this.state.mapStyle.layers || [];
    const selectedLayer =
      layers.length > 0 ? layers[this.state.selectedLayerIndex] : undefined;

    const toolbar = (
      <AppToolbar
        renderer={this._getRenderer()}
        mapState={this.state.mapState}
        mapStyle={this.state.mapStyle}
        inspectModeEnabled={this.state.mapState === "inspect"}
        sources={this.state.sources}
        onStyleChanged={this.onStyleChanged}
        onStyleOpen={this.onStyleChanged}
        onSetMapState={this.setMapState}
        onToggleModal={this.toggleModal.bind(this)}
        editorMode={this.state.editorMode}
        theme={this.state.theme}
        onSetEditorMode={this.onSetEditorMode}
        onToggleTheme={this.onToggleTheme}
        onUndo={this.onUndo}
        onRedo={this.onRedo}
      />
    );

    const layerList = (
      <LayerList
        onMoveLayer={this.onMoveLayer}
        onLayerDestroy={this.onLayerDestroy}
        onLayerCopy={this.onLayerCopy}
        onLayerVisibilityToggle={this.onLayerVisibilityToggle}
        onLayersChange={this.onLayersChange}
        onLayerSelect={this.onLayerSelect}
        selectedLayerIndex={this.state.selectedLayerIndex}
        layers={layers}
        sources={this.state.sources}
        errors={this.state.errors}
      />
    );

    const layerEditor = selectedLayer ? (
      <LayerEditor
        key={this.state.selectedLayerOriginalId}
        layer={selectedLayer}
        layerIndex={this.state.selectedLayerIndex}
        isFirstLayer={this.state.selectedLayerIndex < 1}
        isLastLayer={
          this.state.selectedLayerIndex ===
          this.state.mapStyle.layers.length - 1
        }
        sources={this.state.sources}
        vectorLayers={this.state.vectorLayers}
        spec={this.state.spec}
        onMoveLayer={this.onMoveLayer}
        onLayerChanged={this.onLayerChanged}
        onLayerDestroy={this.onLayerDestroy}
        onLayerCopy={this.onLayerCopy}
        onLayerVisibilityToggle={this.onLayerVisibilityToggle}
        onLayerIdChange={this.onLayerIdChange}
        errors={this.state.errors}
      />
    ) : undefined;

    // Undo and redo messages are developer text ("Undo setPaintProperty
    // water fill-color ..."), so Simple mode shows errors only.
    const infos = this.state.editorMode === "simple" ? [] : this.state.infos;
    const bottomPanel =
      this.state.errors.length + infos.length > 0 ? (
        <MessagePanel
          currentLayer={selectedLayer}
          selectedLayerIndex={this.state.selectedLayerIndex}
          onLayerSelect={this.onLayerSelect}
          mapStyle={this.state.mapStyle}
          errors={this.state.errors}
          infos={infos}
        />
      ) : undefined;

    const modals = (
      <div>
        <ModalDebug
          renderer={this._getRenderer()}
          maplibreGlDebugOptions={this.state.maplibreGlDebugOptions}
          openlayersDebugOptions={this.state.openlayersDebugOptions}
          onChangeMaplibreGlDebug={this.onChangeMaplibreGlDebug}
          onChangeOpenlayersDebug={this.onChangeOpenlayersDebug}
          isOpen={this.state.isOpen.debug}
          onOpenToggle={this.toggleModal.bind(this, "debug")}
          mapView={this.state.mapView}
        />
        <ModalShortcuts
          isOpen={this.state.isOpen.shortcuts}
          onOpenToggle={this.toggleModal.bind(this, "shortcuts")}
        />
        <ModalSettings
          mapStyle={this.state.mapStyle}
          onStyleChanged={this.onStyleChanged}
          onChangeMetadataProperty={this.onChangeMetadataProperty}
          isOpen={this.state.isOpen.settings}
          onOpenToggle={this.toggleModal.bind(this, "settings")}
        />
        <ModalExport
          mapStyle={this.state.mapStyle}
          onStyleChanged={this.onStyleChanged}
          isOpen={this.state.isOpen.export}
          onOpenToggle={this.toggleModal.bind(this, "export")}
        />
        <ModalOpen
          isOpen={this.state.isOpen.open}
          customStyles={this.state.customStyles?.files}
          onStyleOpen={this.openStyle}
          onOpenToggle={this.toggleModal.bind(this, "open")}
        />
        <ModalSave
          isOpen={this.state.isOpen.save}
          onStyleSave={this.saveStyleToCloudflare}
          onStyleOpen={this.openStyle}
          onOpenToggle={this.toggleModal.bind(this, "save")}
          customStyles={this.state.customStyles?.files}
        />
        <ModalSources
          mapStyle={this.state.mapStyle}
          onStyleChanged={this.onStyleChanged}
          isOpen={this.state.isOpen.sources}
          onOpenToggle={this.toggleModal.bind(this, "sources")}
        />
      </div>
    );

    const simplePanel =
      this.state.editorMode === "simple" ? (
        <SimplePanel
          mapStyle={this.state.mapStyle}
          zoom={this.state.mapView.zoom}
          selection={this.state.simpleSelection}
          onSelect={this.onSimpleSelect}
          onSpotlight={this.onSimpleSpotlight}
          onOpenStyle={this.openStyle}
          onPreview={this.onSimplePreview}
          onCommit={this.onSimpleCommit}
          onSession={this.onSimpleSession}
          aiOpen={this.state.isOpen.ai}
          onAiToggle={this.toggleModal.bind(this, "ai")}
          onOpenGallery={this.toggleModal.bind(this, "open")}
          onSwitchToAdvanced={() => this.onSetEditorMode("advanced")}
        />
      ) : undefined;

    return (
      <AppLayout
        toolbar={toolbar}
        layerList={layerList}
        layerEditor={layerEditor}
        map={this.mapRenderer()}
        bottom={bottomPanel}
        modals={modals}
        simplePanel={simplePanel}
      />
    );
  }
}
